import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  TreePine, ChevronDown, ChevronRight, Sparkles, ArrowRight, Check, X,
  Globe, Heart, HandHeart,
} from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { api } from "../lib/api";
import { formatMoney } from "../lib/format";
const MOCK_TOTAL = { BRL: 2500, USD: 800, EUR: 700 };
import { useAuth } from "../context/AuthContext";
import LanguageSelector from "../components/LanguageSelector";
import MarketSelector from "../components/MarketSelector";
import SnowLayer from "../components/SnowLayer";
import { StarField, ShootingStar, Moon } from "../components/Cinematic";
import { MEDIA, MODULE_IMAGES, WORLD_COVERS, srcSet } from "../lib/media";
import { Modal } from "../components/Modal";
import { LANDING } from "../i18n/landingCopy";
import { GuaranteeSeal } from "../components/GuaranteeSeal";
import AmbientMusic from "../components/AmbientMusic";

import { trackInitiateCheckout } from "../lib/pixel";
const track = (event, meta) => { api.post("/analytics/event", { event, meta: meta || {} }).catch(() => {}); };

function daysToChristmas() {
  const today = new Date();
  let y = today.getFullYear();
  let target = new Date(y, 11, 24);
  if (target < today) target = new Date(y + 1, 11, 24);
  return Math.ceil((target - today) / 86400000);
}

export default function Landing({ forcedMarket, initialLang }) {
  const { t, lang, setLang } = useI18n();
  const { user } = useAuth();
  const L = LANDING[lang] || LANDING.pt;
  const [checkout, setCheckout] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [market, setMarket] = useState("BR");
  const [openFaq, setOpenFaq] = useState(0);
  const [showSoon, setShowSoon] = useState(false);
  const [covers, setCovers] = useState([]);
  const [styles, setStyles] = useState([]);
  const [worldC, setWorldC] = useState([]);
  const [showSticky, setShowSticky] = useState(false);
  const [stickyClosed, setStickyClosed] = useState(false);
  const days = daysToChristmas();
  const productRef = useRef(null);
  const demoSeen = useRef(false);

  useEffect(() => {
    api.get(`/christmas/options?lang=${lang}`).then(({ data }) => { setCovers(data.covers || []); setStyles(data.styles || []); }).catch(() => {});
    api.get(`/christmas/world?lang=${lang}`).then(({ data }) => setWorldC(data.countries || [])).catch(() => {});
  }, [lang]);

  useEffect(() => { track("landing_view"); }, []);
  // Legacy landing kept for reference only: keep it out of search engines (avoids duplicate content).
  useEffect(() => {
    const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex, nofollow"; m.setAttribute("data-v1-noindex", "1");
    document.head.appendChild(m);
    return () => { m.remove(); };
  }, []);

  // Market routes (/br,/us,...) set an INITIAL interface language; user can still change it.
  useEffect(() => { if (initialLang) setLang(initialLang); }, [initialLang, setLang]);

  useEffect(() => {
    api.get("/pricing").then(({ data }) => {
      setPricing(data);
      setCheckout(data.checkout || null);
      const saved = localStorage.getItem("mc_market");
      let initial;
      if (forcedMarket && data.markets[forcedMarket]) { initial = forcedMarket; localStorage.setItem("mc_market", forcedMarket); }
      else if (saved && data.markets[saved]) initial = saved;
      else if (data.detected && data.markets[data.detected]) initial = data.detected;
      else initial = data.fallback;
      setMarket(initial);
    }).catch(() => {});
  }, [forcedMarket]);

  const current = pricing?.markets?.[market];
  const marketList = pricing ? pricing.order.map((c) => pricing.markets[c]) : [];
  // Selecting a country manually also switches to that market's default language
  // (country -> language), but changing language never changes the country.
  const MARKET_LANG = { BR: "pt", US: "en", PT: "pt-PT", FR: "fr", ES: "es", IT: "it" };
  const changeMarket = (code) => {
    const previous = market;
    setMarket(code);
    localStorage.setItem("mc_market", code);
    track("country_selected", { country: code, market: code, previous_country: previous });
    const defLang = MARKET_LANG[code];
    if (defLang && defLang !== lang) setLang(defLang); // auto (not logged as manual)
  };

  useEffect(() => {
    const onScroll = () => setShowSticky(window.scrollY > window.innerHeight * 0.9);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const el = productRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting && !demoSeen.current) { demoSeen.current = true; track("view_product_demo"); } });
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [covers]);

  // Preserve campaign attribution (utm_*) captured from the current URL.
  const utms = () => {
    const p = new URLSearchParams(window.location.search);
    const o = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => { if (p.get(k)) o[k] = p.get(k); });
    return o;
  };

  const handleBuy = (source) => {
    const meta = { source, market, country: market, language: lang, landing_route: window.location.pathname,
      displayed_currency: current?.currency, displayed_price: current?.amount, ...utms() };
    track("click_buy", meta);
    const url = checkout?.active ? checkout?.url : "";  // SINGLE global checkout
    if (url) {
      track("checkout_redirect", meta);
      trackInitiateCheckout({ value: current?.amount, currency: current?.currency });
      const q = new URLSearchParams({ ...utms(), lang, market, src: `mc_${market}_${lang}` }).toString();
      window.location.href = url + (url.includes("?") ? "&" : "?") + q;
    } else setShowSoon(true);
  };
  const heroCta = () => { track("hero_cta_click"); handleBuy("hero"); };
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const dashCover = covers.find((c) => c.id === "elegante") || covers[0];

  return (
    <div className="min-h-screen overflow-x-hidden bg-xmas-navy text-xmas-snow">
      {/* Nav */}
      <header className="fixed inset-x-0 top-0 z-50 border-b border-xmas-gold/10 bg-xmas-navy/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-3 py-3 sm:px-6">
          <Link to={user ? "/app" : "/"} className="flex items-center gap-1.5 sm:gap-2" data-testid="brand-logo">
            <TreePine className="h-5 w-5 text-xmas-gold sm:h-6 sm:w-6" />
            <span className="whitespace-nowrap font-display text-[13px] font-bold tracking-wider sm:text-lg sm:tracking-widest">MY CHRISTMAS</span>
          </Link>
          <div className="flex items-center gap-1.5 sm:gap-3">
            {pricing && <MarketSelector compact markets={marketList} value={market} onChange={changeMarket} lang={lang} />}
            <LanguageSelector compact />
            <div className="group relative">
              <Link to={user ? "/app" : "/login"} data-testid="nav-login-button"
                className="block whitespace-nowrap rounded-full border border-xmas-gold/40 px-3 py-1.5 text-xs text-xmas-snow transition hover:border-xmas-gold hover:text-xmas-gold sm:px-4 sm:py-2 sm:text-sm">
                {user ? t("nav.home") : L.login}
              </Link>
              <div className="pointer-events-none absolute right-0 top-full z-50 mt-2 hidden whitespace-nowrap rounded-lg border border-xmas-gold/25 bg-xmas-navy px-3 py-1.5 text-center opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100 sm:block" data-testid="login-tooltip">
                <p className="text-[10px] text-xmas-slate">{t("login.tooltip")}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* 1. HERO */}
      <section className="relative flex min-h-[100svh] items-center justify-center overflow-hidden">
        <img src={MEDIA.hero} srcSet={srcSet(MEDIA.hero)} sizes="100vw" fetchpriority="high" decoding="async" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050A18]/75 via-[#0B132B]/65 to-[#0B132B]" />
        <StarField count={70} /><Moon /><ShootingStar /><SnowLayer count={50} />
        <div className="relative z-20 mx-auto max-w-4xl px-4 text-center" style={{ transform: "translateY(33px)" }}>
          <p className="float-up mb-4 text-xs uppercase tracking-[0.4em] text-xmas-gold sm:text-sm" style={{ animationDelay: "0.1s" }}>MY CHRISTMAS</p>
          <h1 className="float-up font-display text-2xl font-black leading-[1.08] tracking-tight sm:text-4xl lg:text-[2.55rem]" style={{ animationDelay: "0.2s" }}>
            {L.hero.h1a}<br /><span className="gold-gradient-text">{L.hero.h1b}</span>
          </h1>
          <p className="float-up mx-auto mt-6 max-w-2xl text-base text-xmas-snow/85 sm:text-lg" style={{ animationDelay: "0.35s" }}>{L.hero.sub}</p>
          <div className="float-up mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" style={{ animationDelay: "0.5s" }}>
            <button onClick={heroCta} data-testid="hero-start-button" className="btn-gold w-full rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider sm:w-auto">{L.hero.cta}{current?.display ? ` — ${current.display}` : ""}</button>
            <button onClick={() => scrollTo("product")} data-testid="hero-how-button" className="glass inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-semibold uppercase tracking-wider text-xmas-snow transition hover:border-xmas-gold/60 sm:w-auto">{L.hero.cta2} <ChevronDown className="h-4 w-4" /></button>
          </div>
          {current?.display && (
            <div className="float-up mt-7" style={{ animationDelay: "0.58s" }} data-testid="hero-price">
              <p className="text-[11px] uppercase tracking-[0.3em] text-xmas-gold/80">{L.hero.access}</p>
              <p className="mt-1 font-display text-4xl font-black text-xmas-gold sm:text-5xl" data-testid="hero-price-value">{current.display}</p>
              <p className="mt-1.5 text-xs text-xmas-snow/70">{L.hero.oneTime}</p>
              <p className="mt-1 text-[11px] text-xmas-slate" data-testid="hero-price-country">{current.flag} {L.hero.priceFor.replace("{country}", current.name?.[lang] || "")}</p>
            </div>
          )}
          <p className="float-up mt-6 text-xs uppercase tracking-widest text-xmas-gold/70" style={{ animationDelay: "0.64s" }}>{L.hero.badge}</p>
        </div>
      </section>

      {/* 2. PRODUCT MOCKUPS */}
      <section id="product" ref={productRef} className="relative overflow-hidden bg-gradient-to-b from-xmas-navy to-[#12172b] py-20 sm:py-24" data-testid="product-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="text-center font-display text-3xl font-bold sm:text-4xl">{L.product.title}</h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-base text-xmas-slate">{L.product.text}</p>
          <div className="relative mt-14 flex flex-col items-center justify-center gap-6 lg:flex-row lg:items-end">
            {/* Browser frame (dashboard) */}
            <div className="w-full max-w-2xl overflow-hidden rounded-2xl border border-xmas-gold/25 bg-[#0d1120] shadow-2xl" data-testid="mockup-browser">
              <div className="flex items-center gap-1.5 border-b border-white/10 bg-[#161b2e] px-4 py-2.5">
                <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f56]" /><span className="h-2.5 w-2.5 rounded-full bg-[#ffbd2e]" /><span className="h-2.5 w-2.5 rounded-full bg-[#27c93f]" />
                <span className="ml-3 text-[11px] text-xmas-slate">mychristmas · Dashboard</span>
              </div>
              <div className="relative h-56 sm:h-72">
                <img src="/img/opt/dashboard-cover-1280.webp" srcSet="/img/opt/dashboard-cover-640.webp 640w, /img/opt/dashboard-cover-1280.webp 1280w" sizes="(max-width: 640px) 100vw, 640px" alt="" loading="lazy" decoding="async" data-testid="mockup-dashboard-cover"
                  onError={(e) => { if (dashCover?.img && e.currentTarget.src !== dashCover.img) e.currentTarget.src = dashCover.img; }}
                  className="h-full w-full bg-[#1a2140] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <p className="text-[11px] uppercase tracking-widest text-xmas-gold">🎄 {days} · {L.sticky.cta}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {["decoration", "ceia", "table", "gifts", "planner"].map((k) => (
                      <span key={k} className="rounded-full border border-xmas-gold/25 bg-black/30 px-2.5 py-1 text-[11px] text-xmas-snow">{t(`nav.${k}`)}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. PAIN */}
      <section className="relative overflow-hidden py-20" data-testid="pain-section">
        <img src={MEDIA.livingRoom} srcSet={srcSet(MEDIA.livingRoom)} sizes="100vw" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-xmas-navy via-xmas-navy/90 to-xmas-navy" />
        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-serif-elegant text-2xl italic text-xmas-champagne sm:text-4xl">{L.pain.title}</h2>
          <div className="mt-8 flex flex-wrap justify-center gap-2.5">
            {L.pain.questions.map((q, i) => (
              <span key={i} className="rounded-full border border-xmas-gold/20 bg-white/5 px-4 py-2 text-sm text-xmas-snow/85" style={{ animationDelay: `${i * 0.05}s` }}>{q}</span>
            ))}
          </div>
          <p className="mt-10 font-display text-2xl font-bold text-xmas-gold sm:text-3xl">{L.pain.bridge}</p>
          <p className="mx-auto mt-4 max-w-2xl text-base text-xmas-snow/80">{L.pain.text}</p>
          <button onClick={() => handleBuy("pain")} data-testid="pain-cta" className="btn-gold mt-8 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider">{L.pain.cta} <ArrowRight className="h-4 w-4" /></button>
        </div>
      </section>

      {/* 4. DEMO — say your Christmas */}
      <section className="bg-gradient-to-b from-xmas-navy to-[#161a2e] py-20" data-testid="demo-section">
        <div className="mx-auto max-w-5xl px-4 text-center sm:px-6">
          <h2 className="font-display text-3xl font-bold sm:text-4xl lg:text-5xl"><span className="gold-gradient-text">{L.demo.title}</span></h2>
          <div className="mt-12 grid grid-cols-1 items-center gap-6 lg:grid-cols-[1fr_auto_1fr]">
            <div className="glass rounded-2xl p-6 text-left" data-testid="demo-say">
              <p className="mb-4 text-xs uppercase tracking-widest text-xmas-champagne">{L.demo.say}</p>
              {[["🌍", L.personal.formula[0], current?.name?.[lang] || current?.name?.pt || "Brasil"], ["👨‍👩‍👧‍👦", L.personal.formula[1], "10"],
                ["💰", L.personal.formula[2], formatMoney(current?.symbol || "R$", MOCK_TOTAL[current?.currency] || 2500)],
                ["✨", L.personal.formula[3], (L.quiz?.questions?.[0]?.opts || []).find(([k]) => k === "elegante")?.[1] || "Elegante"], ["🎁", t("nav.secretsanta"), t("wiz.yes")]].map(([e, k, v], i) => (
                <div key={i} className="mb-2 flex items-center justify-between rounded-xl border border-xmas-gold/15 bg-white/5 px-4 py-2.5">
                  <span className="text-sm text-xmas-slate"><span className="mr-2">{e}</span>{k}</span><span className="text-sm font-medium text-xmas-snow">{v}</span>
                </div>
              ))}
            </div>
            <ArrowRight className="mx-auto hidden h-8 w-8 text-xmas-gold lg:block" />
            <div className="text-left" data-testid="demo-help">
              <p className="mb-4 font-serif-elegant text-lg italic text-xmas-gold">{L.demo.help}</p>
              <div className="flex flex-wrap gap-2">
                {["decoration", "ceia", "table", "recipes", "gifts", "secretsanta", "planner", "budget"].map((k) => (
                  <span key={k} className="rounded-full border border-xmas-gold/25 px-3 py-1.5 text-sm text-xmas-champagne">{t(`nav.${k}`)}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PERSONALIZATION */}
      <section className="relative overflow-hidden py-20" data-testid="personalization-section">
        <img src={MEDIA.table} srcSet={srcSet(MEDIA.table)} sizes="100vw" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-r from-xmas-navy via-xmas-navy/92 to-xmas-navy/70" />
        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6">
          <h2 className="font-display text-3xl font-bold leading-tight sm:text-4xl"><span className="gold-gradient-text">{L.personal.title}</span></h2>
          <p className="mx-auto mt-5 max-w-2xl text-base text-xmas-snow/80">{L.personal.text}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2.5">
            {L.personal.formula.map((f, i) => (
              <React.Fragment key={f}>
                <span className="rounded-xl border border-xmas-gold/25 bg-white/5 px-4 py-2.5 text-sm text-xmas-snow">{f}</span>
                {i < L.personal.formula.length - 1 && <span className="text-xmas-gold">+</span>}
              </React.Fragment>
            ))}
            <span className="ml-1 font-display text-lg text-xmas-gold">{L.personal.equals}</span>
          </div>
          <button onClick={() => handleBuy("personalization")} data-testid="personal-cta" className="btn-gold mt-9 inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider">{L.personal.cta} <ArrowRight className="h-4 w-4" /></button>
        </div>
      </section>

      {/* 6. FEATURES as benefits (alternating) */}
      <section className="bg-gradient-to-b from-[#161a2e] to-xmas-navy py-20" data-testid="features-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-14 text-center font-display text-3xl font-bold sm:text-4xl">{L.featuresTitle}</h2>
          <div className="space-y-10 sm:space-y-16">
            {L.features.map((f, i) => (
              <div key={f.key} data-testid={`feature-block-${f.key}`} className={`grid items-center gap-6 lg:grid-cols-2 lg:gap-12 ${i % 2 ? "lg:[direction:rtl]" : ""}`}>
                <div className="card-lift overflow-hidden rounded-2xl border border-xmas-gold/15 [direction:ltr]">
                  <img src={MODULE_IMAGES[f.key]} srcSet={srcSet(MODULE_IMAGES[f.key])} sizes="(max-width: 640px) 100vw, 400px" alt="" loading="lazy" decoding="async" className="h-52 w-full object-cover transition duration-700 hover:scale-105 sm:h-64" />
                </div>
                <div className="[direction:ltr]">
                  <h3 className="font-display text-2xl font-bold text-xmas-snow sm:text-3xl">{f.title}</h3>
                  <p className="mt-3 max-w-md text-base text-xmas-slate">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. STYLE QUIZ */}
      <StyleQuiz L={L} styles={styles} onBuy={() => handleBuy("quiz")} />

      {/* 8. RECEIVE */}
      <section className="relative overflow-hidden py-20" data-testid="receive-section">
        <img src={MEDIA.fireplace} srcSet={srcSet(MEDIA.fireplace)} sizes="100vw" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-xmas-navy via-xmas-navy/92 to-xmas-navy" />
        <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6">
          <h2 className="mb-10 text-center font-display text-3xl font-bold sm:text-4xl">{L.receive.title}</h2>
          <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
            {L.receive.items.map((it) => (
              <div key={it} className="flex items-center gap-3 rounded-xl border border-xmas-gold/12 bg-white/5 px-4 py-3">
                <Check className="h-4 w-4 shrink-0 text-xmas-gold" /><span className="text-sm text-xmas-snow">{it}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 9. BEFORE / WITH */}
      <section className="bg-gradient-to-b from-xmas-navy to-[#161a2e] py-20" data-testid="compare-section">
        <div className="mx-auto max-w-5xl px-4 sm:px-6">
          <h2 className="mb-12 text-center font-display text-3xl font-bold sm:text-4xl"><span className="gold-gradient-text">{L.compare.title}</span></h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <p className="mb-4 text-sm uppercase tracking-widest text-xmas-slate">{L.compare.beforeLabel}</p>
              <ul className="space-y-2.5">{L.compare.before.map((b) => <li key={b} className="flex items-center gap-2.5 text-sm text-xmas-slate line-through decoration-xmas-slate/40"><X className="h-4 w-4 shrink-0 text-xmas-slate/60" /> {b}</li>)}</ul>
            </div>
            <div className="rounded-2xl border border-xmas-gold/30 bg-xmas-gold/5 p-6">
              <p className="mb-4 text-sm uppercase tracking-widest text-xmas-gold">{L.compare.withLabel}</p>
              <ul className="space-y-2.5">{L.compare.with.map((w) => <li key={w} className="flex items-center gap-2.5 text-sm text-xmas-snow"><Check className="h-4 w-4 shrink-0 text-xmas-gold" /> {w}</li>)}</ul>
            </div>
          </div>
          <p className="mt-8 text-center font-serif-elegant text-xl italic text-xmas-champagne">{L.compare.foot}</p>
        </div>
      </section>

      {/* 11. INTERNATIONAL */}
      <section className="bg-gradient-to-b from-xmas-navy to-[#12172b] py-20 text-center" data-testid="intl-section">
        <div className="mx-auto max-w-3xl px-4 sm:px-6">
          <Globe className="mx-auto mb-5 h-9 w-9 text-xmas-gold" />
          <h2 className="font-display text-3xl font-bold sm:text-4xl">{L.intl.title}</h2>
          <p className="mx-auto mt-4 max-w-xl text-base text-xmas-slate">{L.intl.text}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">{L.intl.langs.map((lg) => <span key={lg} className="rounded-full border border-xmas-gold/25 bg-white/5 px-5 py-2 text-sm text-xmas-champagne">{lg}</span>)}</div>
          <p className="mt-6 text-xs text-xmas-slate">{L.intl.note}</p>
        </div>
      </section>

      {/* 12. WORLD preview */}
      <section className="bg-[#12172b] py-20" data-testid="world-preview-section">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-10 text-center">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">{L.world.title}</h2>
            <p className="mx-auto mt-3 max-w-xl text-base text-xmas-slate">{L.world.text}</p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {(worldC.length ? worldC : [{ code: "BR", flag: "🇧🇷", name: "Brasil" }, { code: "US", flag: "🇺🇸", name: "USA" }, { code: "ES", flag: "🇪🇸", name: "España" }, { code: "FR", flag: "🇫🇷", name: "France" }, { code: "IT", flag: "🇮🇹", name: "Italia" }, { code: "PT", flag: "🇵🇹", name: "Portugal" }]).map((c, i) => (
              <div key={c.code} data-testid={`world-preview-${c.code}`} className="card-lift group relative overflow-hidden rounded-2xl border border-xmas-gold/15" style={{ animationDelay: `${i * 0.05}s` }}>
                <img src={WORLD_COVERS[c.code] || covers[i % (covers.length || 1)]?.img || MEDIA.table} srcSet={srcSet(WORLD_COVERS[c.code])} sizes="(max-width: 640px) 50vw, 300px" alt="" loading="lazy" decoding="async" className="h-32 w-full object-cover transition duration-700 group-hover:scale-110 sm:h-40" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-3"><span className="text-2xl">{c.flag}</span><p className="text-sm text-xmas-snow">{c.name}</p></div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center"><button onClick={() => handleBuy("world")} data-testid="world-cta" className="glass inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-xmas-snow transition hover:border-xmas-gold/60">{L.world.cta} <ChevronRight className="h-4 w-4" /></button></div>
        </div>
      </section>

      {/* 13. EMOTIONAL */}
      <section className="relative overflow-hidden py-24" data-testid="emotion-section">
        <img src={MEDIA.fireplace} srcSet={srcSet(MEDIA.fireplace)} sizes="100vw" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-xmas-navy/70 via-xmas-navy/85 to-xmas-navy" />
        <SnowLayer count={20} />
        <div className="relative z-10 mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="font-serif-elegant text-2xl italic text-xmas-champagne sm:text-3xl">{L.emotion.h1}</h2>
          <p className="mt-6 font-display text-3xl font-bold text-xmas-gold sm:text-4xl">{L.emotion.h2}</p>
          <p className="mx-auto mt-6 max-w-xl text-base text-xmas-snow/80">{L.emotion.text}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full border border-xmas-gold/25 px-4 py-2 text-sm text-xmas-champagne"><HandHeart className="h-4 w-4 text-xmas-gold" /> {L.emotion.prayer}</span>
            <span className="inline-flex items-center gap-2 rounded-full border border-xmas-gold/25 px-4 py-2 text-sm text-xmas-champagne"><Heart className="h-4 w-4 text-xmas-gold" /> {L.emotion.gratitude}</span>
          </div>
        </div>
      </section>

      {/* 14. HOW IT WORKS */}
      <section className="bg-xmas-navy py-20" data-testid="how-it-works">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <h2 className="mb-12 text-center font-display text-3xl font-bold sm:text-4xl">{L.how.title}</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {L.how.steps.map(([ti, de], i) => (
              <div key={i} className="glass rounded-2xl p-6 text-center" data-testid={`how-step-${i + 1}`}>
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-xmas-gold/20 font-mono-num text-xl font-bold text-xmas-gold">{i + 1}</div>
                <h3 className="font-display text-lg text-xmas-snow">{ti}</h3>
                <p className="mt-2 text-sm text-xmas-slate">{de}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 15. OFFER */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#12172b] to-xmas-navy py-20" data-testid="offer-section">
        <SnowLayer count={16} />
        <div className="relative z-10 mx-auto max-w-2xl px-4 sm:px-6">
          <p className="text-center text-xs uppercase tracking-[0.3em] text-xmas-gold">{L.offer.kicker}</p>
          <div className="mt-5 rounded-3xl border border-xmas-gold/30 bg-xmas-navycard/80 p-7 shadow-2xl sm:p-10">
            <h2 className="text-center font-display text-3xl font-bold text-xmas-snow">{L.offer.title}</h2>
            <div className="mx-auto mt-6 grid max-w-md grid-cols-1 gap-2 sm:grid-cols-2">
              {L.offer.items.map((it) => <div key={it} className="flex items-center gap-2 text-sm text-xmas-snow"><Check className="h-4 w-4 shrink-0 text-xmas-gold" /> {it}</div>)}
            </div>
            <div className="mt-8 rounded-2xl border border-dashed border-xmas-gold/30 bg-white/5 py-6 text-center" data-testid="offer-price">
              {current?.display ? (
                <>
                  <p className="font-display text-4xl font-black text-xmas-gold" data-testid="offer-price-value">{current.display}</p>
                  <p className="mt-2 inline-block rounded-full border border-xmas-gold/30 px-3 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-xmas-gold" data-testid="offer-price-onetime">{L.offer.oneTime}</p>
                  <p className="mt-1 text-xs text-xmas-slate">{current.flag} {current.name?.[lang]} · {current.currency}</p>
                </>
              ) : (
                <p className="text-sm text-xmas-champagne">{L.offer.priceNote}</p>
              )}
            </div>
            <button onClick={() => handleBuy("offer")} data-testid="offer-buy-button" className="btn-gold mt-6 w-full rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider">{L.offer.cta}</button>
            <p className="mt-4 text-center text-xs text-xmas-slate">{L.offer.secure}</p>
          </div>
        </div>
      </section>

      {/* 16. FAQ */}
      <section className="mx-auto max-w-3xl px-4 py-20 sm:px-6" data-testid="faq-section">
        <h2 className="mb-10 text-center font-display text-3xl font-bold sm:text-4xl">FAQ</h2>
        <div className="space-y-3">
          {L.faq.map(([q, a], i) => (
            <div key={i} className="glass overflow-hidden rounded-xl" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)} className="flex w-full items-center justify-between px-5 py-4 text-left">
                <span className="font-medium text-xmas-snow">{q}</span>
                <ChevronDown className={`h-5 w-5 shrink-0 text-xmas-gold transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
              </button>
              {openFaq === i && <p className="px-5 pb-5 text-sm text-xmas-slate">{a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* 17. FINAL CTA */}
      <section className="relative overflow-hidden border-t border-xmas-gold/15 py-24 text-center" data-testid="final-cta-section">
        <img src={MEDIA.fireplace} srcSet={srcSet(MEDIA.fireplace)} sizes="100vw" alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-35" />
        <div className="absolute inset-0 bg-gradient-to-b from-xmas-navy/80 to-xmas-navy" />
        <SnowLayer count={24} />
        <div className="relative z-10 mx-auto max-w-2xl px-4">
          <p className="mb-6 inline-block rounded-full border border-xmas-gold/25 px-4 py-1.5 text-xs text-xmas-champagne" data-testid="countdown">{L.countdown(days)}</p>
          <h2 className="font-serif-elegant text-2xl italic text-xmas-champagne sm:text-3xl">{L.finalCta.h1}</h2>
          <p className="mt-3 font-display text-3xl font-bold text-xmas-snow sm:text-4xl">{L.finalCta.h2}</p>
          <p className="mx-auto mt-5 max-w-lg text-base text-xmas-snow/80">{L.finalCta.text}</p>
          <button onClick={() => handleBuy("final")} data-testid="footer-buy-button" className="btn-gold mt-8 rounded-full px-10 py-4 text-sm font-bold uppercase tracking-wider">{L.finalCta.cta}</button>
          <p className="mt-5 font-serif-elegant text-lg italic text-xmas-gold">{L.finalCta.sign}</p>
          <div className="mt-8 flex justify-center"><GuaranteeSeal size={140} testid="final-guarantee-seal" /></div>
        </div>
      </section>

      <footer className="border-t border-xmas-gold/10 py-8 text-center text-xs text-xmas-slate">
        <p className="font-display tracking-widest text-xmas-gold">MY CHRISTMAS</p>
        <p className="mt-2">© {new Date().getFullYear()} MY CHRISTMAS. {t("footer.rights")}</p>
      </footer>

      {/* 18. STICKY MOBILE CTA */}
      {showSticky && !stickyClosed && (
        <div className="fixed inset-x-0 bottom-0 z-50 flex items-center justify-between gap-3 border-t border-xmas-gold/25 bg-xmas-navy/95 px-4 py-3 backdrop-blur-xl lg:hidden" data-testid="sticky-cta">
          <span className="font-display text-sm font-bold tracking-widest text-xmas-gold">MY CHRISTMAS</span>
          <div className="flex items-center gap-2">
            <button onClick={() => handleBuy("sticky")} data-testid="sticky-buy-button" className="btn-gold rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider">{L.sticky.cta}</button>
            <button onClick={() => setStickyClosed(true)} aria-label="close" className="text-xmas-slate"><X className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      <Modal open={showSoon} onClose={() => setShowSoon(false)} title={t("checkout.soon_title")} testid="checkout-soon-modal">
        <p className="text-sm text-xmas-slate">{t("checkout.soon")}</p>
      </Modal>
      <AmbientMusic />
    </div>
  );
}

function StyleQuiz({ L, styles, onBuy }) {
  const [started, setStarted] = useState(false);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  const begin = () => { setStarted(true); setStep(0); setAnswers([]); setResult(null); track("start_style_quiz"); };
  const pick = (styleId) => {
    const next = [...answers, styleId];
    if (step < L.quiz.questions.length - 1) { setAnswers(next); setStep(step + 1); }
    else {
      const counts = {};
      next.forEach((s) => { counts[s] = (counts[s] || 0) + 1; });
      const winner = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      const style = styles.find((s) => s.id === winner) || styles[0];
      setResult(style); setAnswers(next); track("complete_style_quiz", { style: winner });
    }
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-xmas-navy to-[#161a2e] py-20" data-testid="quiz-section">
      <div className="mx-auto max-w-2xl px-4 text-center sm:px-6">
        {!started && (
          <>
            <Sparkles className="mx-auto mb-4 h-8 w-8 text-xmas-gold" />
            <h2 className="font-display text-3xl font-bold sm:text-4xl">{L.quiz.cta}</h2>
            <p className="mt-3 text-sm text-xmas-slate">{L.quiz.intro}</p>
            <button onClick={begin} data-testid="quiz-start-button" className="btn-gold mt-7 rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider">{L.quiz.cta}</button>
          </>
        )}
        {started && !result && (
          <div data-testid="quiz-question">
            <div className="mb-6 h-1.5 overflow-hidden rounded-full bg-xmas-navysurface"><div className="h-full rounded-full bg-gradient-to-r from-xmas-pine to-xmas-gold transition-all" style={{ width: `${((step + 1) / L.quiz.questions.length) * 100}%` }} /></div>
            <h3 className="mb-6 font-display text-2xl text-xmas-snow">{L.quiz.questions[step].q}</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {L.quiz.questions[step].opts.map(([val, label]) => (
                <button key={label} onClick={() => pick(val)} data-testid={`quiz-opt-${val}`} className="rounded-full border border-xmas-gold/25 px-5 py-3 text-sm text-xmas-snow transition hover:border-xmas-gold hover:bg-xmas-gold/10">{label}</button>
              ))}
            </div>
          </div>
        )}
        {result && (
          <div data-testid="quiz-result">
            <p className="text-xs uppercase tracking-widest text-xmas-gold">{L.quiz.resultKicker}</p>
            <h3 className="mt-2 font-display text-3xl font-bold text-xmas-snow sm:text-4xl">{result?.name}</h3>
            {result?.img && <img src={result.img} alt="" className="mx-auto mt-6 h-48 w-full max-w-md rounded-2xl border border-xmas-gold/20 object-cover" />}
            {result?.desc && <p className="mx-auto mt-4 max-w-md text-base text-xmas-slate">{result.desc}</p>}
            {result?.palette && <div className="mt-4 flex justify-center gap-2">{result.palette.map((c) => <span key={c} className="h-6 w-6 rounded-full border border-white/20" style={{ background: c }} />)}</div>}
            <p className="mx-auto mt-6 max-w-md font-serif-elegant text-lg italic text-xmas-champagne">{L.quiz.resultQ}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button onClick={onBuy} data-testid="quiz-result-cta" className="btn-gold rounded-full px-8 py-4 text-sm font-bold uppercase tracking-wider">{L.quiz.resultCta}</button>
              <button onClick={begin} className="glass rounded-full px-6 py-4 text-sm font-semibold text-xmas-snow transition hover:border-xmas-gold/60">{L.quiz.again}</button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
